// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; the field captions are visual labels for the adjacent control (same convention as SupplierAdmin / the UserAdmin* lists, Task #25).
// biome-ignore-all lint/suspicious/noArrayIndexKey: the pager's "…" separators are in a wholesale-replaced, stable-order list (same rationale as ImportPage / DeliveryAddressImport, Task #32).
//
// =============================================================================
// SchoolsAdmin — the Schools admin screen (Manage → Schools)
// =============================================================================
// Admin/SuperUser editor for the `schools` reference table: searchable list with
// deactivate/reactivate, "+ New school" and Edit via the Unleashed-style
// SchoolFormModal, plus (added in Batch 4) the ACARA CSV import section. Reads go
// through the src/data/schools repo (server-side filtered + capped — never read
// the ~10,900-row table unfiltered); the modal + toggle own the writes.
// Reference: docs/schools-admin-import-plan.md.
// =============================================================================
import React from "react";
import { listSchoolIdsWithByod } from "../../data/schoolByod";
import { listSchools, SCHOOLS_LIST_LIMIT, updateSchoolById } from "../../data/schools";
import { confirmDialog } from "../../services/dialogService";
import { logAudit } from "../../utils/auditLog";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";
import { SchoolFormModal } from "./SchoolFormModal.jsx";
import { SchoolsImport } from "./SchoolsImport.jsx";

const STATE_FILTER = ["All", "NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const LIST_GRID = {
  gridTemplateColumns: "96px 1fr 150px 56px 92px 44px 96px 190px",
};

// Compact pager: always show the first + last page, a small window around the
// current page, and "…" for gaps. Returns an array of 0-based page indexes and
// "…" strings. (Exported for a unit test.)
export function pageItems(current, totalPages) {
  const want = new Set([0, totalPages - 1, current - 1, current, current + 1]);
  const valid = [...want].filter((p) => p >= 0 && p < totalPages).sort((a, b) => a - b);
  const out = [];
  let prev = -1;
  for (const p of valid) {
    if (prev >= 0 && p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

export function SchoolsAdmin() {
  const [rows, setRows] = React.useState([]);
  // Set of school ids (on the current page) that have an active BYOD policy —
  // drives the "BYOD Policy" YES column. Loaded per page after the rows land.
  const [byodIds, setByodIds] = React.useState(new Set());
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [stateFilter, setStateFilter] = React.useState("All");
  const [savedMsg, setSavedMsg] = React.useState("");
  const [refreshTick, setRefreshTick] = React.useState(0);
  // null | { mode: "create" | "edit", row }
  const [modal, setModal] = React.useState(null);
  const [showImport, setShowImport] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [total, setTotal] = React.useState(0);

  // Server-side filtering (PostgREST caps un-limited selects at 1,000 rows, and
  // there are ~10,900 schools). Re-query on each filter change, debounced while
  // typing; the repo applies the row limit.
  React.useEffect(
    function () {
      let cancelled = false;
      setLoading(true);
      const t = setTimeout(
        function () {
          listSchools({
            search: search,
            state: stateFilter === "All" ? undefined : stateFilter,
            page: page,
          }).then(function (data) {
            if (cancelled) return;
            if (data) {
              setRows(data.rows);
              setTotal(data.total);
              // Which of this page's schools have a BYOD policy (db-198) — one
              // lookup over the page's ids, mapped to the YES column below.
              const ids = data.rows.map(function (r) {
                return r.id;
              });
              listSchoolIdsWithByod(ids).then(function (set) {
                if (!cancelled) setByodIds(set);
              });
            }
            setLoading(false);
          });
        },
        search ? 250 : 0
      );
      return function () {
        cancelled = true;
        clearTimeout(t);
      };
    },
    [search, stateFilter, page, refreshTick]
  );

  function refresh() {
    setRefreshTick(function (n) {
      return n + 1;
    });
  }

  function showSaved(msg) {
    setSavedMsg(msg || "Saved ✓");
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
  }

  function onModalSaved() {
    const wasCreate = modal && modal.mode === "create";
    refresh();
    showSaved(wasCreate ? "School created ✓" : "Saved ✓");
  }

  async function toggleDeactivate(r) {
    if (!r.deactivated) {
      const ok = await confirmDialog({
        title: "Deactivate school",
        message: `Deactivate "${r.name}"? It will be hidden from the request wizard's school search. You can reactivate it here at any time.`,
        confirmLabel: "Deactivate",
        danger: true,
      });
      if (!ok) return;
    }
    const next = !r.deactivated;
    const { error } = await updateSchoolById(r.id, { deactivated: next });
    if (error) {
      showSaved("Update failed");
      return;
    }
    logAudit(next ? "school_deactivated" : "school_reactivated", {
      id: r.id,
      name: r.name,
      acara_id: r.acara_id,
    });
    refresh();
    showSaved(next ? "School deactivated ✓" : "School reactivated ✓");
  }

  // Rows are one server-side page; `total` drives the pager.
  const shown = rows;
  const totalPages = Math.ceil(total / SCHOOLS_LIST_LIMIT);
  const rangeFrom = page * SCHOOLS_LIST_LIMIT;

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="m-0 text-[22px] text-zephyr-text">Schools</h1>
        {savedMsg && <span className="text-[13px] text-[#2E7D32] font-semibold">{savedMsg}</span>}
      </div>
      <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
        The school reference list behind the request wizard's school search. Add, edit or deactivate
        a school, or bulk-import from the ACARA "Australian Schools List" export below.
      </p>

      <div className="flex gap-4 items-center mb-4 flex-wrap">
        <input
          value={search}
          onChange={function (e) {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Search name, suburb or ACARA ID…"
          className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white w-[300px]"
          style={{ fontFamily: "inherit" }}
        />
        <select
          aria-label="Filter by state"
          value={stateFilter}
          onChange={function (e) {
            setStateFilter(e.target.value);
            setPage(0);
          }}
          className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
          style={{ fontFamily: "inherit" }}
        >
          {STATE_FILTER.map(function (s) {
            return (
              <option key={s} value={s}>
                {s === "All" ? "All states" : s}
              </option>
            );
          })}
        </select>
        <span className="text-[12px] text-zephyr-muted">
          {total === 0
            ? "No schools"
            : `Showing ${rangeFrom + 1}–${rangeFrom + rows.length} of ${total} school${
                total === 1 ? "" : "s"
              }`}
        </span>
        <Btn
          variant="secondary"
          onClick={function () {
            setShowImport(true);
          }}
          style={{ marginLeft: "auto" }}
        >
          Import from CSV
        </Btn>
        <Btn
          onClick={function () {
            setModal({ mode: "create", row: null });
          }}
        >
          + New school
        </Btn>
      </div>

      {loading ? (
        <div className="text-center text-zephyr-muted p-10">Loading...</div>
      ) : (
        <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
          <div
            className="grid gap-0 bg-zephyr-teal-dark text-white py-[7px] px-[13px] text-[11px] font-bold uppercase tracking-[0.5px]"
            style={LIST_GRID}
          >
            <div>ACARA</div>
            <div>Name</div>
            <div>Suburb</div>
            <div className="text-center">State</div>
            <div>Type</div>
            <div className="text-center">SL</div>
            <div className="text-center">BYOD Policy</div>
            <div>Actions</div>
          </div>
          {shown.map(function (r, idx) {
            return (
              <div
                key={r.id}
                style={{
                  borderBottom: idx < shown.length - 1 ? "1px solid #CFE0DE" : "none",
                  background: r.deactivated ? "#FFF3F3" : idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                }}
              >
                <div className="grid gap-0 py-[7px] px-[13px] items-center" style={LIST_GRID}>
                  <div className="text-[12px] text-zephyr-muted">{r.acara_id || "—"}</div>
                  <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                    <button
                      type="button"
                      onClick={function () {
                        setModal({ mode: "edit", row: r });
                      }}
                      className="bg-transparent border-0 p-0 font-semibold text-[13px] underline cursor-pointer text-left"
                      style={{
                        color: r.deactivated ? "#607D79" : "#1A2E2B",
                        fontFamily: "inherit",
                      }}
                    >
                      {r.name}
                    </button>
                    {r.deactivated && (
                      <span className="ml-[6px] text-[10px] font-bold text-white bg-[#C62828] rounded-[3px] py-[1px] px-[6px]">
                        DEACTIVATED
                      </span>
                    )}
                  </div>
                  <div className="text-[13px] text-zephyr-text overflow-hidden text-ellipsis whitespace-nowrap">
                    {r.suburb || "—"}
                  </div>
                  <div className="text-center text-[13px] text-zephyr-text">{r.state || "—"}</div>
                  <div className="text-[12px] text-zephyr-muted overflow-hidden text-ellipsis whitespace-nowrap">
                    {r.school_type || "—"}
                  </div>
                  <div className="text-center">
                    {r.school_sl ? (
                      <span className="text-[11px] font-bold text-white rounded px-[6px] py-[2px] bg-zephyr-teal">
                        SL
                      </span>
                    ) : (
                      <span className="text-[12px] text-zephyr-muted">—</span>
                    )}
                  </div>
                  <div className="text-center">
                    {byodIds.has(r.id) ? (
                      <span className="text-[11px] font-bold text-[#2E7D32]">YES</span>
                    ) : (
                      <span className="text-[12px] text-zephyr-muted">—</span>
                    )}
                  </div>
                  <div className="flex gap-[6px]">
                    <button
                      type="button"
                      onClick={function () {
                        setModal({ mode: "edit", row: r });
                      }}
                      className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer border border-[#CFE0DE] text-[#1A2E2B]"
                      style={{ fontFamily: "inherit" }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={function () {
                        toggleDeactivate(r);
                      }}
                      className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer border"
                      style={{
                        fontFamily: "inherit",
                        borderColor: r.deactivated ? "#2E7D32" : "#E0A0A0",
                        color: r.deactivated ? "#2E7D32" : "#C62828",
                      }}
                    >
                      {r.deactivated ? "Reactivate" : "Deactivate"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {shown.length === 0 && (
            <div className="p-10 text-center text-zephyr-muted text-[13px]">No schools found.</div>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex gap-1 items-center justify-center mt-4 text-[13px] flex-wrap">
          <button
            type="button"
            disabled={page === 0}
            onClick={function () {
              setPage(function (p) {
                return Math.max(0, p - 1);
              });
            }}
            className="px-[10px] py-1 rounded-[6px] border border-zephyr-border bg-white"
            style={{
              fontFamily: "inherit",
              opacity: page === 0 ? 0.4 : 1,
              cursor: page === 0 ? "default" : "pointer",
            }}
          >
            ‹ Prev
          </button>
          {pageItems(page, totalPages).map(function (it, idx) {
            if (it === "…") {
              return (
                <span key={`ellipsis-${idx}`} className="px-2 text-zephyr-muted">
                  …
                </span>
              );
            }
            const isCurrent = it === page;
            return (
              <button
                key={it}
                type="button"
                onClick={function () {
                  setPage(it);
                }}
                className={[
                  "px-[10px] py-1 rounded-[6px] border cursor-pointer",
                  isCurrent
                    ? "bg-zephyr-teal text-white border-zephyr-teal font-bold"
                    : "bg-white border-zephyr-border text-zephyr-text",
                ].join(" ")}
                style={{ fontFamily: "inherit" }}
              >
                {it + 1}
              </button>
            );
          })}
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={function () {
              setPage(function (p) {
                return Math.min(totalPages - 1, p + 1);
              });
            }}
            className="px-[10px] py-1 rounded-[6px] border border-zephyr-border bg-white"
            style={{
              fontFamily: "inherit",
              opacity: page >= totalPages - 1 ? 0.4 : 1,
              cursor: page >= totalPages - 1 ? "default" : "pointer",
            }}
          >
            Next ›
          </button>
        </div>
      )}

      {/* ACARA CSV bulk import — opens from the top toolbar as a modal. */}
      {showImport && (
        <ModalBackdrop
          onClose={function () {
            setShowImport(false);
          }}
          portal
          contentStyle={{ maxWidth: 680 }}
        >
          <SchoolsImport
            onImported={refresh}
            onClose={function () {
              setShowImport(false);
            }}
          />
        </ModalBackdrop>
      )}

      {modal && (
        <SchoolFormModal
          mode={modal.mode}
          row={modal.row}
          onClose={function () {
            setModal(null);
          }}
          onSaved={onModalSaved}
        />
      )}
    </div>
  );
}
